import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useToast } from '../../Utils/toastContext';

export default function PrivacyPolicyViewer() {
  const BASE_URL = process.env.REACT_APP_BASE_URL;
  const navigate = useNavigate();
  const { showToast } = useToast();
  const [sections, setSections] = useState([]);
  const [version, setVersion] = useState('');
  const [UpdatedDate, setUpdatedDate] = useState('');
  const renderRichText = (doc) => {
    if (!doc?.content) return null;

    return doc.content.map((block, i) => {
      if (block.type !== 'paragraph') return null;

      return (
        <p key={i} className="mb-4 leading-7 text-slate-700 text-sm">
          {block.content?.map((node, idx) => {
            const isBold = node.marks?.some((m) => m.type === 'bold');

            return (
              <span
                key={idx}
                className={isBold ? 'font-semibold text-slate-900' : ''}
              >
                {node.text}
              </span>
            );
          })}
        </p>
      );
    });
  };
  const GetAPIcalls = async (url) => {
    const token = localStorage.getItem('jwtToken');
    const apiUrl = `${BASE_URL}${url}`;
    const headers = {
      'Content-Type': 'application/json',
    };

    if (token) {
      headers.Authorization = `Bearer ${token}`;
    }

    try {
      const response = await fetch(apiUrl, {
        method: 'GET',
        headers,
      });

      if (!response.ok) {
        if (response.status === 401) {
          localStorage.removeItem('jwtToken');
          navigate('/login');
          throw new Error('Session expired. Please log in again.');
        }
        const errorData = await response.json().catch(() => ({
          message: 'An unexpected error occurred',
        }));
        throw new Error(errorData.message || 'Something went wrong');
      }

      return await response.json();
    } catch (error) {
      console.error('API Call Error:', error); // Log full error for debugging
      throw new Error(
        typeof error.message === 'string'
          ? error.message
          : 'Network error, please try again later',
      );
    }
  };
  const extractTextDeep = (node) => {
    if (!node) return '';

    // direct text
    if (typeof node.text === 'string') return node.text;

    // nested doc structure
    if (node.type === 'doc' && Array.isArray(node.content)) {
      return node.content.map(extractTextDeep).join('');
    }

    // paragraph level
    if (Array.isArray(node.content)) {
      return node.content.map(extractTextDeep).join('');
    }

    return '';
  };
  const mapApiToUI = (resource) => {
    if (!resource) return;

    const payload = resource.content_payload;

    const mappedSections = payload.sections.map((sec) => {
      // ---------------- PARAGRAPH ----------------
      if (sec.type === 'paragraph') {
        const text = sec.content?.content?.[0]?.content?.[0]?.text || '';
        //const text = extractTextDeep(sec.content);

        return {
          id: Date.now() + Math.random(),
          type: 'paragraph',
          content: text,
        };
      }

      // ---------------- HEADING ----------------
      if (sec.type === 'heading') {
        return {
          id: Date.now() + Math.random(),
          type: 'heading',
          content: sec.content || '',
        };
      }

      // ---------------- LIST (FIXED) ----------------
      if (sec.type === 'list') {
        const items = Array.isArray(sec.content)
          ? sec.content.map((item) => {
              // normal case
              if (typeof item?.text === 'string') return item.text;

              // fallback nested case
              return extractTextDeep(item);
            })
          : [];

        return {
          id: Date.now() + Math.random(),
          type: 'list',
          content: items,
        };
      }

      // ---------------- FALLBACK ----------------
      return {
        id: Date.now() + Math.random(),
        type: sec.type,
        content: sec.content || '',
      };
    });

    // setTitle(payload.title || "Privacy Policy");
    setSections(mappedSections);
  };
  const getPrivacyPolicy = async () => {
    try {
      const response = await GetAPIcalls(`/api/v1/content-resources`);

      if (response.status == 200) {
        mapApiToUI(response.data.resources[0]);
        setVersion(response.data.resources[0].version_number);
        setUpdatedDate(response.data.resources[0].updated_at);
      } else {
        if (response.status === 401) {
          localStorage.removeItem('jwtToken');
          navigate('/login');
        }
      }
    } catch (err) {
      showToast(err.message, 'error');
    }
  };
  useEffect(() => {
    getPrivacyPolicy();
  }, []);
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50 p-6 md:p-10">
      {/* HEADER CARD */}
      <div className="max-w-4xl mx-auto bg-white rounded-3xl shadow-sm border p-6 md:p-10 mb-6">
        <div className="flex items-start justify-between flex-wrap gap-3">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-slate-900">
              Privacy Policy
            </h1>

            <p className="text-sm text-slate-500 mt-1">
              Version {version} • Last updated {UpdatedDate}
            </p>
          </div>
        </div>

        {/* INFO BANNER */}
        <div className="mt-6 p-4 rounded-2xl bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-100">
          <p className="text-xs text-slate-600 leading-relaxed">
            This document explains how your personal information is collected,
            used, and protected by Ratified Pty Ltd.
          </p>
        </div>
      </div>

      {/* CONTENT */}
      <div className="max-w-4xl mx-auto bg-white border rounded-3xl shadow-sm p-6 md:p-10 space-y-6">
        {sections.map((section, index) => {
          // ---------------- HEADING ----------------
          if (section.type === 'heading') {
            return (
              <h2
                key={index}
                className="text-xl md:text-2xl font-bold text-slate-900 mt-8 first:mt-0
                     border-l-4 border-blue-500 pl-4 py-1 bg-blue-50/40 rounded-r-xl"
              >
                {section.content}
              </h2>
            );
          }

          // ---------------- PARAGRAPH (Rich JSON) ----------------
          if (
            section.type === 'paragraph' &&
            typeof section.content === 'object'
          ) {
            return (
              <div
                key={index}
                className="text-sm md:text-[15px] leading-7 text-slate-700"
              >
                {renderRichText(section.content)}
              </div>
            );
          }

          // ---------------- PARAGRAPH (STRING) ----------------
          if (section.type === 'paragraph') {
            return (
              <p
                key={index}
                className="text-sm md:text-[15px] leading-7 text-slate-700"
              >
                {section.content}
              </p>
            );
          }

          // ---------------- LIST ----------------
          if (section.type === 'list') {
            return (
              <div key={index} className="pl-2">
                <ul className="space-y-3">
                  {section.content.map((item, i) => (
                    <li
                      key={i}
                      className="flex items-start gap-3 text-sm md:text-[15px] text-slate-700"
                    >
                      <span className="mt-1 text-blue-500">•</span>
                      <span className="leading-6">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            );
          }

          return null;
        })}
      </div>

      {/* FOOTER */}
      <div className="max-w-4xl mx-auto mt-10 text-center text-xs text-slate-500">
        © {new Date().getFullYear()} Ratified Pty Ltd
      </div>
    </div>
  );
}
